// Pure logic for the tariff table. Deterministic so the selection rules and the mutation
// payloads can be unit tested without mounting the table or touching Apollo.

import { ALGO_TYPES } from './constants';

/**
 * Algo options offered in the Type picker: public ones, plus any non-public algo this account
 * already has an override for.
 * NOTE: the override list is not filtered by tariff type — that is how it has always behaved,
 * and narrowing it could hide an algo an account is genuinely using.
 */
export const algoOptionsFor = (tariffType, overrides) => {
    const inUse = (overrides || []).map((o) => o.algo_type);
    return (ALGO_TYPES[tariffType] || []).filter((algo) => algo.public || inUse.includes(algo.value));
};

/**
 * Display label for a tariff's algo. Looked up against the full list rather than the filtered
 * picker options, so a retired non-public algo still renders its name instead of a blank.
 */
export const algoLabel = (tariffType, algoType) =>
    (ALGO_TYPES[tariffType] || []).find((algo) => algo.value === algoType)?.label || '';

/** The account-wide order override (a tariff, no partner scoping it), or undefined when there is none. */
export const defaultOverrideFor = (overrides) =>
    (overrides || []).find((override) => override.tariff_id && !override.partner_client_id);

/** The account-wide driver mapping (not pinned to a driver), or undefined when there is none. */
export const defaultMappingFor = (driverMappings) => (driverMappings || []).find((m) => !m.driver_id);

export const tariffsOfType = (tariffs, tariffType) => (tariffs || []).filter((t) => t.tariff_type === tariffType);

export const isDefaultTariff = (tariff, defaultObj) =>
    !!tariff?.tariff_id && tariff.tariff_id === defaultObj?.tariff_id;

/**
 * Payload that repoints the account-wide driver mapping at `tariff`. `mapping_id` is included
 * only when one already exists, so the mutation inserts the first time and updates after.
 */
export const driverMappingPayload = ({ defaultMapping, client_id, tariff }) => ({
    mappings: [
        {
            ...(defaultMapping?.mapping_id ? { mapping_id: defaultMapping.mapping_id } : {}),
            client_id,
            driver_id: null,
            tariff_id: tariff.tariff_id,
        },
    ],
});

/**
 * Payload that repoints the account-wide order override at `tariff`. `algo_type` travels with
 * it because the override records which algo priced the order, and `oms: true` is what scopes
 * the row to this surface — the list query filters on it.
 */
export const pricingOverridePayload = ({ defaultOverride, client_id, tariff }) => ({
    override: {
        pricing_override_id: defaultOverride?.pricing_override_id,
        client_id,
        partner_client_id: null,
        tariff_id: tariff.tariff_id,
        algo_type: tariff.algo_type,
        oms: true,
    },
});

/** Insert payload for a new tariff. `fields` carries extra columns, such as the Order tab's category and commodity tags. */
export const createTariffPayload = ({ client_id, algo, tariffType, ...fields }) => ({
    tariffs: [{ client_id, algo_type: algo, tariff_type: tariffType, ...fields }],
});
